import React, {useState, useRef} from "react"
import {HELPER, isValidEmail} from "../prodect/ProsCommonUi";
import BasicButton, {ConfirmButton} from "./BasicButton";
import {isValueEmpty} from "../prodect/ProsConnect";

// Example
// <TextInput
// size={"string"} //sm, xs
// label={"string"}
// type={"search"} //email, search, number, default text
// value={"string"}
// isValid={boolean}
// disabled={boolean}
// required={boolean}
// isControlled={boolean}       // Uses controlled mode when true reflecting props.value changes
// onChange={function}
// onSubmit={function}
// />

export default function TextInput(props) {
    const [pasted, setPasted] = useState('')
    const [isValid, setIsValid] = useState(props.isValid == null ? true : props.isValid)//isValid only shows validity if props.required is true
    const [isTyping, setIsTyping] = useState(false)
    const [isHilited, setIsHilited] = useState(props.hilite || false)
    const [value, setValue] = useState(props.value || '')
    const [open, setOpen] = useState(false)
    const inputRef = useRef()
    let lineHeight = 28
    let isDisabled = props.disabled
    let isRequired = props.required || false

    React.useEffect(() => {
        setValue('')
    }, [props.id])

    React.useEffect(() => {
        setIsValid(props.isValid)
    }, [props.isValid])

    React.useEffect(() => {
        setIsHilited(props.hilite)
    }, [props.hilite])

    React.useEffect(() => {
        if (props.isControlled) {
            setValue(props.value)
        }
    }, [props.value, props.isControlled])

    function onFocus(){
        setIsTyping(true)
        if(props.size || !props.multiline || !inputRef.current) return//no support for sizes yet, TextButton causes inputRef to be undefined
        inputRef.current.style.minHeight = 'calc(var(--element-size)'
        if(isInputEmpty()){
            inputRef.current.style.height = 'inherit'
        }else{
            //Guestimate line-height
            setOpen(props.multiline)
            inputRef.current.style.height = isContentGreater() ? inputRef.current.scrollHeight + 'px' : 'inherit'
        }
    }

    function onBlur(evt){
        setIsTyping(false)
        if(typeof props.onBlur === 'function'){
            props.onBlur(evt)
        }
    }

    function renderShrinkButton(){
        if(props.size || !props.multiline || !inputRef.current) return null//no support for sizes yet, TextButton causes inputRef to be undefined
        if(isInputEmpty() || !isContentGreater() || !open) return null
        return <BasicButton
                icon={"chevron-up"}
                size={"sm"}
                action={()=>{
                    inputRef.current.style.minHeight = 'calc(var(--element-size)'
                    inputRef.current.style.height = 'inherit'
                    setOpen(false)
                    setIsTyping(false)
                }}
        />
    }

    function isInputEmpty(){
        return inputRef.current && inputRef.current.value.length < 1
    }

    function isContentGreater(){
        //(props.multiline + 1) because one line doesnt need srhink button
        return inputRef.current.scrollHeight >= (props.multiline + 1) * lineHeight * 0.8//Mumbojumbo
    }

    function renderClearBtn(){
        if(isInputEmpty() || isDisabled || props.readOnly) return null
        return <BasicButton
            title="Tyhjennä"
            action={()=>{
                setValue('')
                inputRef.current.value = ''
                if(typeof props.onChange === 'function') props.onChange('')
                if(typeof props.forceReset === 'function') props.forceReset()//Used with textButton & TimerTextInput
                onFocus()
            }}
            icon={"times"}
            iconSize={props.size ? props.size : "xs"}
            size={"sm"}
            tabIndex={-1}
        />
    }

    function hasValueAndSubmitButton(){
        if(props.hideSubmitButton) return false
        if((value === '' && value.length < 1) || isDisabled) return false// && isRequired
        if(typeof props.onSubmit !== 'function' || (typeof isValid === 'boolean' && !isValid)) return false
        return true
    }

    function renderSubmitBtn(){
        if(!hasValueAndSubmitButton()) return null
        return <ConfirmButton
            hideLabel
            disabled={isDisabled}
            action={onSubmit}
            icon={props.icon || "check"}
            iconSize={props.size ? props.size : null}
            size={props.size || null}
            btnClass={props.btnClass || "primary"}
        />
    }

    function onSubmit(){
        if(!props.disabled && typeof props.onSubmit === 'function') {
            props.onSubmit(inputRef.current.value.replace(/\n$/, ""))//remove last line break)
        }
    }

    function onInputChange(evt){
        if(!props.disabled){
            const val = pasted.length > 0 ? pasted : evt.target.value
            setPasted('')
            if(typeof props.onChange === 'function'){
                props.onChange(val)
            }
            setValue(val)
        }
    }

    function isFieldValid(value, inputType){
        if(isValid == null) return true
        if(isValueEmpty(value)) return false
        if(value.length < 1) return false
        if(inputType === 'email' && !isValidEmail(value, true)) return false
        return isValid//validation should come from props by default me thinks now
    }

    function renderInputElement(){
        const inputType = props.type || 'text'
        const TextTag = !!props.multiline ? 'textarea' : 'input'
        const inputIsValid = isFieldValid(value, inputType)
        return <div
                hidden={props.hidden}
                onBlur={onBlur}
                className={"label-set"
                    + (isRequired && !inputIsValid ? " label-set-required" : "")
                    + (inputIsValid && isRequired ? " label-set-ok" : "")
                    + (isTyping ? " label-set-active" : "")
                    + (hasValueAndSubmitButton() ? " has-value" : "")
                    + (inputType === 'search' ? " type-" + inputType : "")}
                data-tooltip={inputType === 'search' && !props.size && value?.length < 1 ? HELPER.search + (props.tooltip ? ". " + props.tooltip[0] : "") : null}
            >
                {props.label ? <label className={props.labelCSS || null} htmlFor={props.id || null}>{props.label}</label> : null}
                <div className={props.customButton ? "has-button" : null}>
                    <TextTag
                        ref={inputRef}
                        id={props.id || null}
                        tabIndex={props.tabIndex || null}
                        onFocus={onFocus}
                        onClick={onFocus}
                        type={!props.multiline ? inputType : null}
                        disabled={props.disabled}
                        readOnly={props.readOnly || null}
                        inputMode={props.inputMode || (props.type === 'number' ? 'numeric' : null)}
                        defaultValue={props.isControlled ? undefined : value}
                        value={props.isControlled ? value : undefined}
                        placeholder={props.placeholder || 'Kirjoita...'}
                        required={isRequired}
                        rows={props.multiline ? props.multiline : null}
                        onKeyUp={props.onKeyUp || null}
                        onChange={(e)=> {
                            if(props.disabled) return
                            onInputChange(e)
                            onFocus(e)
                        }}
                        onPaste={(e)=>{
                            if(props.disabled) return
                            const selectionStart = inputRef.current.selectionStart
                            const selectionEnd = inputRef.current.selectionEnd
                            const beforeSelection = value ? value.substring(0, selectionStart) : ''
                            const afterSelection = value ? value.substring(selectionEnd, value.length) : ''
                            setPasted(beforeSelection + e.clipboardData.getData('text') + afterSelection)
                        }}
                        autoFocus={props.autoFocus || undefined}//FOLLOW: this seems to cause problems when multiple TextInputs are rendered
                        onKeyDown={(e)=> {
                            if(props.disabled) return
                            //check if onSubmit is function to allow external onSubmits
                            if (e.key === 'Enter' && !e.shiftKey && typeof props.onSubmit === 'function') {
                                e.preventDefault()
                                e.stopPropagation()
                                onSubmit()
                            }
                            if(props.onKeyDown) props.onKeyDown(e)
                        }}
                    />
                    {props.customButton}
                    {renderClearBtn()}
                    {renderShrinkButton()}
                </div>
                {renderSubmitBtn()}
            </div>
    }

    return <div
            //TBC autoComplete="on"
            className={"textinput" + (props.className ? " " + props.className : "")
                + (isHilited ? " textinput-hilite" : "")
                + (props.size ? " textinput-" + props.size : "")
            }
            data-tooltip={props['data-tooltip'] || null}
        >
            {renderInputElement()}
            {props.info ? <span className={"text-sm"}>{props.info}</span> : null}
        </div>
}

//Used only for search
export function TimerTextInput(props){
    let timerID = useRef(null)

    function setTimer(val){
        timerID.current = setTimeout(()=>{
            clearTimer()
            doSubmit(val)
        }, 3000)//keystroke search was 800ms, so this is sort of 3.75 times that. See -> DEFERRED_SEARCH_DELAY_MS
    }
    function clearTimer(){
        clearTimeout(timerID.current)
    }

    const doSubmit = (t)=> {
        clearTimer()
        props.timerCallback(t)
    }

    function onBlur(evt){
        if(evt.target.value === '') {
            doSubmit('')
        }
    }

    return <TextInput
        {...props}
        onBlur={onBlur}
        onChange={val =>{
            if(val === ''){
                setTimer('')
            }else{
                if(timerID.current != null) clearTimer()
            }
        }}
        onSubmit={(val)=> {
            doSubmit(val)
        }}
        forceReset={()=> doSubmit('')}
        className={"timer-textinput"}
        type={"search"}
        tooltip={["Paina lopuksi 'enter'"]}
        timerCallback={null}
    />
}